import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  orderNumber: "",
  subject: "",
  message: "",
  privacyConsent: false,
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const errorSummaryRef = useRef(null);

  const handleInputChange = (event) => {
    const { checked, name, type, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject) {
      newErrors.subject = "Please select a subject";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    if (!formData.privacyConsent) {
      newErrors.privacyConsent = "Please confirm that you have read the privacy notice";
    }

    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      window.requestAnimationFrame(() => errorSummaryRef.current?.focus());
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    await new Promise((resolve) => window.setTimeout(resolve, 450));
    setFormData(initialFormData);
    setIsSubmitting(false);
    setShowSuccess(true);
  };

  const getErrorId = (name) => (errors[name] ? `contact-${name}-error` : undefined);

  return (
    <main className="content-page editorial-page contact" id="main-content">
      <div className="content-page__inner editorial-page__inner">
        <header className="content-page__header editorial-page__hero">
          <p className="content-page__eyebrow">Customer care</p>
          <h1 className="content-page__title">Contact {siteConfig.brandName}</h1>
          <p className="content-page__intro">
            Questions about fit, fibres or an order? Send a note and our small team
            will help.
          </p>
        </header>

        <div className="editorial-page__split contact__layout">
          <aside className="contact__details" aria-labelledby="contact-details-title">
            <h2 id="contact-details-title">How to reach us</h2>
            <dl className="contact__details-list">
              <div className="contact__detail">
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${siteConfig.contact.email}`}>
                    {siteConfig.contact.email}
                  </a>
                </dd>
              </div>
              <div className="contact__detail">
                <dt>Studio</dt>
                <dd>{siteConfig.contact.address}</dd>
              </div>
              <div className="contact__detail">
                <dt>Customer care hours</dt>
                <dd>{siteConfig.contact.customerCareHours}</dd>
              </div>
            </dl>
            <p className="contact__response-note">
              We usually reply within two working days. Including your order number
              helps us find an existing order more quickly.
            </p>
          </aside>

          <section className="contact__form-section" aria-labelledby="contact-form-title">
            <h2 id="contact-form-title">Send us a message</h2>

            {showSuccess && (
              <div className="form-alert form-alert--success" role="status" tabIndex="-1">
                <h3>Thank you for writing.</h3>
                <p>
                  Your message has been recorded. A reply will be sent to your email address.
                </p>
                <button
                  className="form-button form-button--text"
                  type="button"
                  onClick={() => setShowSuccess(false)}
                >
                  Send another message
                </button>
              </div>
            )}

            {!showSuccess && (
              <form className="form contact__form" noValidate onSubmit={handleSubmit}>
                {Object.keys(errors).length > 0 && (
                  <div
                    className="form-alert form-alert--error"
                    role="alert"
                    tabIndex="-1"
                    ref={errorSummaryRef}
                  >
                    <h3>There is a problem with your message</h3>
                    <ul>
                      {Object.entries(errors).map(([name, message]) => (
                        <li key={name}>
                          <a href={`#contact-${name}`}>{message}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="form-row form-row--two-columns">
                  <div className="form-field">
                    <label className="form-label" htmlFor="contact-firstName">
                      First name <span aria-hidden="true">*</span>
                    </label>
                    <input
                      className="form-input"
                      id="contact-firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(errors.firstName)}
                      aria-describedby={getErrorId("firstName")}
                      required
                    />
                    {errors.firstName && (
                      <p className="form-error" id="contact-firstName-error">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="contact-lastName">
                      Last name <span aria-hidden="true">*</span>
                    </label>
                    <input
                      className="form-input"
                      id="contact-lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(errors.lastName)}
                      aria-describedby={getErrorId("lastName")}
                      required
                    />
                    {errors.lastName && (
                      <p className="form-error" id="contact-lastName-error">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="form-row form-row--two-columns">
                  <div className="form-field">
                    <label className="form-label" htmlFor="contact-email">
                      Email <span aria-hidden="true">*</span>
                    </label>
                    <input
                      className="form-input"
                      id="contact-email"
                      name="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={getErrorId("email")}
                      required
                    />
                    {errors.email && (
                      <p className="form-error" id="contact-email-error">
                        {errors.email}
                      </p>
                    )}
                  </div>

                </div>

                <div className="form-row form-row--two-columns">
                  <div className="form-field">
                    <label className="form-label" htmlFor="contact-subject">
                      Subject <span aria-hidden="true">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="contact-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(errors.subject)}
                      aria-describedby={getErrorId("subject")}
                      required
                    >
                      <option value="">Choose a subject</option>
                      <option value="product">Product and sizing</option>
                      <option value="order">Order help</option>
                      <option value="shipping">Shipping</option>
                      <option value="return">Return or exchange</option>
                      <option value="care">Garment care</option>
                      <option value="other">Something else</option>
                    </select>
                    {errors.subject && (
                      <p className="form-error" id="contact-subject-error">
                        {errors.subject}
                      </p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="contact-orderNumber">
                      Order number <span className="form-optional">(optional)</span>
                    </label>
                    <input
                      className="form-input"
                      id="contact-orderNumber"
                      name="orderNumber"
                      type="text"
                      autoComplete="off"
                      value={formData.orderNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label className="form-label" htmlFor="contact-message">
                    Message <span aria-hidden="true">*</span>
                  </label>
                  <textarea
                    className="form-textarea"
                    id="contact-message"
                    name="message"
                    rows="7"
                    value={formData.message}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={getErrorId("message")}
                    required
                  />
                  {errors.message && (
                    <p className="form-error" id="contact-message-error">
                      {errors.message}
                    </p>
                  )}
                </div>

                <div className="form-field form-field--checkbox">
                  <input
                    className="form-checkbox"
                    id="contact-privacyConsent"
                    name="privacyConsent"
                    type="checkbox"
                    checked={formData.privacyConsent}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.privacyConsent)}
                    aria-describedby={getErrorId("privacyConsent")}
                    required
                  />
                  <div>
                    <label className="form-label" htmlFor="contact-privacyConsent">
                      I have read the <Link to="/privacy">privacy notice</Link> and
                      agree to the handling of my message as described.
                    </label>
                    {errors.privacyConsent && (
                      <p className="form-error" id="contact-privacyConsent-error">
                        {errors.privacyConsent}
                      </p>
                    )}
                  </div>
                </div>

                <p className="form-required-note">
                  <span aria-hidden="true">*</span> Required fields
                </p>
                <button className="form-button" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

export default Contact;
